import React, { useState } from 'react';
import { MapPin, Phone, Clock, Send, MessageCircle, ExternalLink } from 'lucide-react';
import Button from '../common/Button';
import Card from '../common/Card';
import Toast from '../common/Toast';

const ShowroomPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [showToast, setShowToast] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const subject = encodeURIComponent(formData.subject || "Nouveau message depuis la page Showroom");
        const bodyText = `Nom Complet: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`;
        const body = encodeURIComponent(bodyText);

        window.location.href = `mailto:contacts@keurfayeameublement.sn?subject=${subject}&body=${body}`;

        setShowToast(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
    };

    return (
        <div className="bg-white pt-24 pb-20">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1540518614846-7eded433c457?auto=format&fit=crop&q=80&w=1920"
                    alt="Showroom"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-brand-dark/40 flex items-center justify-center text-center px-6">
                    <div className="max-w-4xl">
                        <h1 className="text-5xl md:text-7xl font-extrabold text-white mb-6 drop-shadow-2xl">
                            Notre Showroom
                        </h1>
                        <p className="text-xl md:text-2xl text-white/90 font-medium max-w-2xl mx-auto drop-shadow-lg">
                            Venez découvrir l'excellence du design et le confort de nos collections dans un espace dédié à l'inspiration.
                        </p>
                    </div>
                </div>
            </section>

            <div className="container mx-auto px-6 -mt-16 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Info Cards */}
                    <Card className="p-8 flex flex-col items-center text-center hover:translate-y-[-8px] transition-all duration-300">
                        <div className="w-16 h-16 bg-brand-dark/5 rounded-2xl flex items-center justify-center text-brand mb-6">
                            <MapPin className="w-8 h-8" />
                        </div>

                        <h3 className="text-xl font-bold text-brand-dark mb-4">
                            Nos adresses
                        </h3>

                        <div className="text-slate-600 space-y-2">
                            <p>Marché Bou Bess, Guédiawaye, Sénégal</p>
                            <p>Cité Apecsy, Yoff, Sénégal</p>
                        </div>
                    </Card>

                    <Card className="p-8 flex flex-col items-center text-center hover:translate-y-[-8px] transition-all duration-300">
                        <div className="w-16 h-16 bg-brand-dark/5 rounded-2xl flex items-center justify-center text-brand mb-6">
                            <Clock className="w-8 h-8" />
                        </div>
                        <h3 className="text-xl font-bold text-brand-dark mb-3">Horaires d'Ouverture</h3>
                        <p className="text-slate-600">
                            Lundi - Samedi: 09:00 - 20:00<br />
                            Dimanche: 10:00 - 15:00
                        </p>
                    </Card>

                    <Card className="p-8 flex flex-col items-center text-center hover:translate-y-[-8px] transition-all duration-300">
                        <div className="w-16 h-16 bg-brand-dark/5 rounded-2xl flex items-center justify-center text-brand mb-6">
                            <Phone className="w-8 h-8" />
                        </div>

                        <h3 className="text-xl font-bold text-brand-dark mb-3">
                            Contactez-nous
                        </h3>

                        <p className="text-slate-600 mb-2">
                            <span className="block">78 138 74 74</span>
                            <span className="block">71 140 68 06</span>
                        </p>

                        <p className="text-slate-600">
                            contacts@keurfayeameublement.sn
                        </p>
                    </Card>
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-24">
                    {/* Left: Location & Experience */}
                    <div className="space-y-12">
                        <div>
                            <h2 className="text-4xl font-extrabold text-brand-dark mb-6">Une Expérience Unique</h2>
                            <p className="text-lg text-slate-600 leading-relaxed mb-6">
                                Nos showrooms ne sont pas seulement des magasins de meubles, ce sont des destinations. Situés a Guediawaye et a Yoff, nos espaces ont été conçus pour vous permettre de toucher, ressentir et visualiser comment nos pièces s'intégreront dans votre foyer.
                            </p>
                            <p className="text-lg text-slate-600 leading-relaxed">
                                Nos conseillers experts sont à votre disposition pour vous accompagner dans vos projets d'aménagement intérieur, du choix des matériaux à la configuration finale de vos meubles.
                            </p>
                        </div>

                        {/* Map Embed (Placeholder using iframe) */}
                        <div className="rounded-3xl overflow-hidden shadow-2xl border-8 border-white h-[400px] bg-slate-100 relative group">
                            <iframe
                                src="https://www.google.com/maps/embed?pb=!1m12!1m8!1m3!1d30867.760775836262!2d-17.4363264!3d14.7431278!3m2!1i1024!2i768!4f13.1!2m1!1sCité%20Apecsy%20Yoff%20OR%20Marché%20bou%20bess%20Guediawaye!5e0!3m2!1sfr!2ssn!4v1704746000000!5m2!1sfr!2ssn"
                                width="100%"
                                height="100%"
                                style={{ border: 0 }}
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                title="Localisation des Showrooms"
                                className="grayscale hover:grayscale-0 transition-all duration-700"
                            ></iframe>
                            <div className="absolute bottom-6 right-6">
                                <Button variant="primary" className="shadow-xl" onClick={() => window.open('https://maps.google.com')}>
                                    <ExternalLink className="w-5 h-5 mr-2" />
                                    Ouvrir dans Google Maps
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Right: Contact Form */}
                    <div className="bg-brand-dark/5 rounded-[2.5rem] p-8 md:p-12">
                        <div className="mb-10">
                            <h2 className="text-3xl font-bold text-brand-dark mb-4">Envoyez-nous un Message</h2>
                            <p className="text-slate-600">
                                Une question sur un produit ou un projet d'aménagement ? Notre équipe vous répondra sous 24h.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-brand-dark ml-1">Nom Complet</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Votre nom"
                                        className="w-full h-14 px-6 rounded-2xl bg-white border-2 border-transparent focus:border-brand focus:ring-0 transition-all outline-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-brand-dark ml-1">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="votre@email.com"
                                        className="w-full h-14 px-6 rounded-2xl bg-white border-2 border-transparent focus:border-brand focus:ring-0 transition-all outline-none"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-brand-dark ml-1">Sujet</label>
                                <input
                                    type="text"
                                    name="subject"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="De quoi souhaitez-vous discuter ?"
                                    className="w-full h-14 px-6 rounded-2xl bg-white border-2 border-transparent focus:border-brand focus:ring-0 transition-all outline-none"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-brand-dark ml-1">Message</label>
                                <textarea
                                    rows="5"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="Dites-nous tout..."
                                    className="w-full px-6 py-4 rounded-3xl bg-white border-2 border-transparent focus:border-brand focus:ring-0 transition-all outline-none resize-none"
                                ></textarea>
                            </div>

                            <Button variant="primary" type="submit" className="w-full h-16 text-lg shadow-xl shadow-brand-dark/10">
                                <Send className="w-5 h-5 mr-4" />
                                Envoyer le message
                            </Button>
                        </form>

                        <div className="mt-12 pt-12 border-t border-brand-dark/10">
                            <h4 className="text-sm font-bold text-brand-dark uppercase tracking-widest mb-6">Contact Instantané</h4>
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-white rounded-2xl border border-brand-dark/5 shadow-sm">
                                    <span className="text-sm font-bold text-slate-500 min-w-[80px]">Guediawaye</span>
                                    <div className="flex flex-wrap gap-2">
                                        <a
                                            href="https://wa.me/221781387474"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-4 py-2 bg-brand/5 hover:bg-brand/10 rounded-xl text-brand font-bold transition-all text-sm"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                            WhatsApp (78 138 74 74)
                                        </a>
                                        <a
                                            href="tel:+221781387474"
                                            className="flex items-center gap-2 px-4 py-2 bg-brand/5 hover:bg-brand/10 rounded-xl text-brand font-bold transition-all text-sm"
                                        >
                                            <Phone className="w-4 h-4" />
                                            Appeler
                                        </a>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 bg-white rounded-2xl border border-brand-dark/5 shadow-sm">
                                    <span className="text-sm font-bold text-slate-500 min-w-[80px]">Yoff</span>
                                    <div className="flex flex-wrap gap-2">
                                        <a
                                            href="https://wa.me/221711406806"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-2 px-4 py-2 bg-brand/5 hover:bg-brand/10 rounded-xl text-brand font-bold transition-all text-sm"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                            WhatsApp (71 140 68 06)
                                        </a>
                                        <a
                                            href="tel:+221711406806"
                                            className="flex items-center gap-2 px-4 py-2 bg-brand/5 hover:bg-brand/10 rounded-xl text-brand font-bold transition-all text-sm"
                                        >
                                            <Phone className="w-4 h-4" />
                                            Appeler
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Toast
                message="Message envoyé avec succès ! Nous vous recontacterons bientôt."
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div>
    );
};

export default ShowroomPage;
